import {
  CloudUpload,
  ExpandLess,
  ExpandMore,
  FileUpload,
  FolderZip,
  InsertDriveFile,
  Visibility,
  VisibilityOff,
  Tune
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useTranslation } from "_metronic/i18n/language";
import React, { useState } from "react";
import { DEFAULT_BACKUP_ZIP_PASSWORD } from "../../constants/backup";
import { ImportItem } from "../../types";
import { loadImportFiles } from "../../utils/importFiles";

type Props = {
  files: File[];
  disabled?: boolean;
  onLoaded: (files: File[], items: ImportItem[]) => void;
  onClean: () => void;
};

const InputMultFiles: React.FC<Props> = ({
  files,
  disabled = false,
  onLoaded,
  onClean
}) => {
  const translate = useTranslation();
  const [reading, setReading] = useState(false);
  const [error, setError] = useState("");
  const [zipPassword, setZipPassword] = useState(DEFAULT_BACKUP_ZIP_PASSWORD);
  const [showPassword, setShowPassword] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const fileIdentity = (file: File) =>
    `${file.name}:${file.size}:${file.lastModified}`;

  const processFiles = async (nextFiles: File[]) => {
    setReading(true);
    setError("");
    try {
      const items = await loadImportFiles(nextFiles, zipPassword);
      onLoaded(nextFiles, items);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Não foi possível ler os arquivos selecionados"
      );
    } finally {
      setReading(false);
    }
  };

  const addFiles = async (selectedFiles: File[]) => {
    if (!selectedFiles.length) return;

    const existingFiles = new Set(files.map(fileIdentity));
    const newFiles = selectedFiles.filter(
      file => !existingFiles.has(fileIdentity(file))
    );
    if (!newFiles.length) {
      setError("Os arquivos selecionados já estão na lista.");
      return;
    }

    await processFiles([...files, ...newFiles]);
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = Array.from(event.target.files ?? []);
    event.target.value = "";
    await addFiles(selectedFiles);
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (disabled || reading) return;
    await addFiles(Array.from(event.dataTransfer.files ?? []));
  };

  const handleRemoveFile = async (fileToRemove: File) => {
    const identityToRemove = fileIdentity(fileToRemove);
    const remainingFiles = files.filter(
      file => fileIdentity(file) !== identityToRemove
    );

    if (!remainingFiles.length) {
      setError("");
      onClean();
      return;
    }

    await processFiles(remainingFiles);
  };

  return (
    <Card>
      <CardHeader
        title="Importar backups"
        subheader="Adicione ZIPs ou JSONs de um ou mais aparelhos"
        action={
          <Button
            size="small"
            color="inherit"
            startIcon={<Tune />}
            endIcon={advancedOpen ? <ExpandLess /> : <ExpandMore />}
            onClick={() => setAdvancedOpen(current => !current)}
            disabled={disabled || reading}
          >
            Configurações
          </Button>
        }
      />
      <CardContent>
        {reading && <LinearProgress sx={{ mb: 2 }} />}

        <Collapse in={advancedOpen}>
          <Alert severity="info" sx={{ mb: 2 }}>
            A senha padrão já é aplicada automaticamente. Altere apenas para
            backups que utilizem outra senha.
          </Alert>
          <TextField
            label="Senha dos backups ZIP"
            type={showPassword ? "text" : "password"}
            value={zipPassword}
            onChange={event => setZipPassword(event.target.value)}
            disabled={disabled || reading}
            sx={{ width: { xs: "100%", sm: 380 }, mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                    onClick={() => setShowPassword(current => !current)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Collapse>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <input
          accept="application/json,.json,application/zip,.zip"
          style={{ display: "none" }}
          id="import-json-or-zip"
          type="file"
          multiple
          disabled={disabled || reading}
          onChange={handleFileChange}
        />

        {!files.length ? (
          <Box
            onDragOver={event => {
              event.preventDefault();
              if (!disabled && !reading) setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={event => void handleDrop(event)}
            sx={{
              border: "2px dashed",
              borderColor: isDragging ? "primary.main" : "divider",
              borderRadius: 2,
              backgroundColor: isDragging ? "action.selected" : "action.hover",
              textAlign: "center",
              px: 2,
              py: { xs: 4, sm: 6 },
              transition: "all 150ms ease",
            }}
          >
            <CloudUpload color="primary" sx={{ fontSize: 56, mb: 1 }} />
            <Typography variant="h6">Arraste os backups para cá</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Você pode selecionar vários arquivos ZIP ou JSON de uma só vez
            </Typography>
            <label htmlFor="import-json-or-zip">
              <Button
                variant="contained"
                color="success"
                component="span"
                startIcon={<FileUpload />}
                disabled={disabled || reading}
                style={{ color: "#fff" }}
              >
                {reading ? "Lendo arquivos..." : "Selecionar JSONs ou ZIPs"}
              </Button>
            </label>
          </Box>
        ) : (
          <Stack spacing={2}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              gap={1}
            >
              <Typography>
                <strong>{files.length}</strong> arquivo(s) selecionado(s)
              </Typography>
              <Stack direction="row" gap={1} flexWrap="wrap">
                <label htmlFor="import-json-or-zip">
                  <Button
                    component="span"
                    variant="contained"
                    color="success"
                    startIcon={<FileUpload />}
                    disabled={disabled || reading}
                    style={{ color: "#fff" }}
                  >
                    {reading ? "Lendo arquivos..." : "Adicionar arquivos"}
                  </Button>
                </label>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => {
                    setError("");
                    onClean();
                  }}
                  disabled={disabled || reading}
                >
                  {translate("buttons_clean")}
                </Button>
              </Stack>
            </Stack>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                p: 1.5,
                borderRadius: 2,
                backgroundColor: "action.hover",
              }}
            >
              {files.map(file => (
                <Chip
                  key={fileIdentity(file)}
                  icon={
                    file.name.toLowerCase().endsWith(".zip") ? (
                      <FolderZip />
                    ) : (
                      <InsertDriveFile />
                    )
                  }
                  label={file.name}
                  title={`${file.name} — clique no X para remover`}
                  variant="outlined"
                  disabled={disabled || reading}
                  onDelete={() => void handleRemoveFile(file)}
                  sx={{ backgroundColor: "background.paper" }}
                />
              ))}
            </Box>
            </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default InputMultFiles;
